import { Client } from "@microsoft/microsoft-graph-client";
import { SPEAuthProvider } from "./speAuthProvider";
import { settingsService } from "./settingsService";

// Azure best practice: Define constants at the top level for better maintainability
// Standard Container Type ID created via PowerShell SharePoint Online Management Shell
const SPE_CONTAINER_TYPE_ID = "94d2cf79-a208-4cc3-8ac7-1ff4348d3fb8";

// Cache for preview URLs to avoid repeated API calls
interface PreviewUrlCache {
  url: string;
  timestamp: number;
}

const previewUrlCache = new Map<string, PreviewUrlCache>();
const PREVIEW_URL_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * SharePoint Embedded Container for Legal Matter Management
 */
export interface SPEContainer {
  id: string;
  displayName: string;
  description?: string;
  containerTypeId: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  settings?: {
    isMatterContainer?: boolean;
    matterId?: string;
    clientId?: string;
  };
}

/**
 * Document metadata from SharePoint Embedded
 */
export interface SPEDocument {
  id: string;
  name: string;
  size: number;
  createdDateTime: string;
  lastModifiedDateTime: string;
  webUrl: string;
  downloadUrl?: string;
  mimeType?: string;
  file?: {
    hashes?: {
      sha1Hash?: string;
      sha256Hash?: string;
    };
  };
  createdBy?: {
    user?: {
      displayName?: string;
      email?: string;
    };
  };
  lastModifiedBy?: {
    user?: {
      displayName?: string;
      email?: string;
    };
  };
}

/**
 * Folder metadata from SharePoint Embedded
 * Supports hybrid navigation with both ID-based performance and path-based breadcrumbs
 */
export interface SPEFolder {
  id: string;
  name: string;
  path: string;
  parentPath: string;
  itemCount?: number;
  createdDateTime: string;
  lastModifiedDateTime: string;
  webUrl?: string;
  createdBy?: {
    user?: {
      displayName?: string;
      email?: string;
    };
  };
  lastModifiedBy?: {
    user?: {
      displayName?: string;
      email?: string;
    };
  };
}

/**
 * Configuration for folder operations
 */
export interface FolderOperationConfig {
  conflictBehavior: 'fail' | 'replace' | 'rename';
  validateDepth?: boolean;
  maxDepth?: number;
}

/**
 * Permission interface for SharePoint Embedded containers
 */
export interface SPEContainerPermission {
  id: string;
  roles: string[];
  grantedToV2: {
    group?: {
      id: string;
      displayName: string;
    };
    user?: {
      id: string;
      displayName: string;
    };
  };
}

/**
 * Request interface for creating container permissions
 */
export interface CreatePermissionRequest {
  roles: string[];
  grantedToV2: {
    group: {
      id: string;
    };
  };
}

/**
 * Service for SharePoint Embedded operations specific to Legal Matter Management
 * Follows Azure best practices for Graph API interactions
 */
export class SharePointEmbeddedService {
  private client: Client;
  private authProvider: SPEAuthProvider;

  constructor(authProvider: SPEAuthProvider) {
    this.authProvider = authProvider;
    
    // Azure best practice: Initialize Graph client with auth provider and beta endpoint
    this.client = Client.init({
      authProvider: async (done: (error: Error | null, token: string | null) => void) => {
        try {
          const token = await authProvider.getAccessToken();
          done(null, token);
        } catch (error) {
          done(error as Error, null);
        }
      },
      // Azure best practice: Try v1.0 endpoint first for SharePoint Embedded
      defaultVersion: 'v1.0'
    });
  }

  /**
   * Creates a new SharePoint Embedded container for a legal matter
   * @param matterId Matter ID for container association
   * @param matterTitle Matter title for container naming
   * @param clientId Client ID for container organization
   * @param description Optional container description
   */
  public async createMatterContainer(
    matterId: string, 
    matterTitle: string, 
    clientId: string,
    description: string = ""
  ): Promise<SPEContainer> {
    try {
      // Azure best practice: Use proper error handling and logging
      const containerName = `Matter-${matterId}-${matterTitle.replace(/[^a-zA-Z0-9-]/g, '_')}`;
      console.info("Creating SharePoint Embedded container for matter:", { matterId, containerName });
      
      // Log the request details for debugging
      const requestPayload = {
        displayName: containerName,
        description: description || `Document container for matter: ${matterTitle}`,
        containerTypeId: SPE_CONTAINER_TYPE_ID,
        settings: {
          isMatterContainer: true,
          matterId: matterId,
          clientId: clientId
        }
      };
      
      console.info("SPE Container creation request payload:", requestPayload);
      
      // Azure best practice: Use correct containerTypeId when creating containers
      const response = await this.client
        .api('/storage/fileStorage/containers')
        .version('v1.0')
        .post(requestPayload);
      
      console.info("SharePoint Embedded container created successfully:", response.id);
      
      // Assign group permissions to the newly created container
      try {
        await this.assignGroupPermissionToContainer(response.id);
        console.info("Group permissions assigned successfully to container:", response.id);
      } catch (permissionError) {
        console.warn("Failed to assign group permissions to container:", permissionError);
        // Don't fail the container creation if permission assignment fails
        // The container is still created, just without the group permissions
      }
      
      return response as SPEContainer;
    } catch (error) {
      console.error("Error creating SPE container for matter:", error);
      
      // Enhanced error logging for debugging
      if (error && typeof error === 'object') {
        console.error("Error details:", {
          message: (error as any).message,
          code: (error as any).code,
          statusCode: (error as any).statusCode,
          body: (error as any).body,
          response: (error as any).response
        });
      }
      
      // Azure best practice: Provide specific error handling for known issues
      if (error instanceof Error) {
        if (error.message.includes("Expired Container type")) {
          throw new Error(`SharePoint Embedded Container Type is expired. Please register a new Container Type in SharePoint Admin Center. Current Container Type ID: ${SPE_CONTAINER_TYPE_ID}`);
        }
        if (error.message.includes("Container type")) {
          throw new Error(`Invalid SharePoint Embedded Container Type. Please verify the Container Type ID: ${SPE_CONTAINER_TYPE_ID}`);
        }
        if (error.message.includes("Access denied") || error.message.includes("Forbidden")) {
          throw new Error(`Access denied creating container. This might indicate: 1) SharePoint Embedded not enabled for tenant, 2) Container type not properly configured, or 3) Billing configuration issue. Container Type ID: ${SPE_CONTAINER_TYPE_ID}`);
        }
      }
      
      throw new Error(`Failed to create matter container: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Gets a specific container by ID
   * @param containerId Container ID to retrieve
   */
  public async getContainer(containerId: string): Promise<SPEContainer | null> {
    try {
      // Use v1.0 endpoint - works for container owners and properly configured permissions
      const response = await this.client
        .api(`/storage/fileStorage/containers/${containerId}`)
        .version('v1.0')  // Reverted back to v1.0 since owner account works fine
        .get();
      
      return response as SPEContainer;
    } catch (error) {
      console.error("Error getting SPE container:", error);
      
      // Enhanced error logging for permission debugging
      if (error instanceof Error) {
        console.error("Container access error details:", {
          containerId,
          message: error.message,
          stack: error.stack
        });
        
        if (error.message.includes('Forbidden') || error.message.includes('admin operations')) {
          console.error("❌ Permission Error: User lacks admin permissions to access container metadata");
          console.info("💡 This is normal for regular users - they should use drive API instead");
          console.info("💡 Admin API is only for SharePoint Embedded administrators");
        }
        
        if (error.message.includes('404')) {
          console.warn("⚠️ Container not found - it may have been deleted");
          return null; // Container not found
        }
      }
      
      throw new Error(`Failed to get container: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Alternative method to verify container access using drive API
   * This may work when direct container access fails due to permission issues
   */
  public async verifyContainerAccess(containerId: string): Promise<boolean> {
    try {
      // Try accessing the container via the drive API which may have different permission requirements
      await this.client
        .api(`/drives/${containerId}`)
        .version('v1.0')  // Use v1.0 for consistency
        .get();
      
      console.info("✅ Container accessible via drive API:", containerId);
      return true;
    } catch (error) {
      console.warn("❌ Container not accessible via drive API:", containerId, error);
      return false;
    }
  }

  /**
   * Enhanced container retrieval with fallback methods
   * Prioritizes drive API for regular users to avoid admin permission requirements
   */
  public async getContainerWithFallback(containerId: string): Promise<SPEContainer | null> {
    console.info(`🔍 Attempting to access container ${containerId} with fallback strategy`);
    
    // For regular users with group permissions, drive API works better than admin container API
    // Try drive API first to avoid "admin operations" permission errors
    console.info("⚡ Trying drive API first (better for group-permissioned users)...");
    const canAccess = await this.verifyContainerAccess(containerId);
    if (canAccess) {
      // If drive API works, create a minimal container object
      console.info("✅ Container accessible via drive API - user has proper group permissions");
      return {
        id: containerId,
        displayName: `Container-${containerId}`,
        containerTypeId: SPE_CONTAINER_TYPE_ID,
        createdDateTime: new Date().toISOString(),
        lastModifiedDateTime: new Date().toISOString(),
        settings: {
          isMatterContainer: true
        }
      };
    }
    
    // If drive API fails, try the admin container API as fallback (for admin users)
    try {
      console.info("💼 Drive API failed, trying admin container API...");
      return await this.getContainer(containerId);
    } catch (adminError) {
      console.error("❌ Both drive API and admin container API failed");
      console.error("Drive API accessible:", false);
      console.error("Admin API error:", adminError instanceof Error ? adminError.message : String(adminError));
      
      // If both methods fail, throw a descriptive error
      throw new Error(`Container ${containerId} is not accessible. User may not be in the correct Microsoft 365 Group or container may not exist.`);
    }
  }

  /**
   * Lists all containers for the current user's legal matters
   * Azure best practice: Include required filters in Graph API queries
   */
  public async getMatterContainers(): Promise<SPEContainer[]> {
    try {
      console.log("Attempting to list containers with containerTypeId:", SPE_CONTAINER_TYPE_ID);
      
      // Try multiple approaches to get containers, starting with the most specific
      let response;
      let attemptedMethods = [];

      // Method 1: Try with v1.0 endpoint and proper GUID format
      try {
        console.log("Method 1: Trying v1.0 endpoint with GUID filter");
        response = await this.client
          .api('/storage/fileStorage/containers')
          .version('v1.0')
          .filter(`containerTypeId eq ${SPE_CONTAINER_TYPE_ID}`)
          .get();
        attemptedMethods.push("v1.0 with filter - SUCCESS");
        console.log("✅ Method 1 successful");
      } catch (v1Error) {
        attemptedMethods.push(`v1.0 with filter - FAILED: ${v1Error instanceof Error ? v1Error.message : String(v1Error)}`);
        console.warn("❌ Method 1 failed:", v1Error);

        // Method 2: Try beta endpoint with filter
        try {
          console.log("Method 2: Trying beta endpoint with GUID filter");
          response = await this.client
            .api('/storage/fileStorage/containers')
            .version('beta')
            .filter(`containerTypeId eq ${SPE_CONTAINER_TYPE_ID}`)
            .get();
          attemptedMethods.push("beta with filter - SUCCESS");
          console.log("✅ Method 2 successful");
        } catch (betaError) {
          attemptedMethods.push(`beta with filter - FAILED: ${betaError instanceof Error ? betaError.message : String(betaError)}`);
          console.warn("❌ Method 2 failed:", betaError);

          // Method 3: Try without filter and filter client-side
          try {
            console.log("Method 3: Trying without filter (client-side filtering)");
            response = await this.client
              .api('/storage/fileStorage/containers')
              .version('v1.0')
              .get();
            attemptedMethods.push("v1.0 without filter - SUCCESS");
            console.log("✅ Method 3 successful");
            
            // Filter client-side
            if (response.value) {
              const originalCount = response.value.length;
              response.value = response.value.filter((container: SPEContainer) => 
                container.containerTypeId === SPE_CONTAINER_TYPE_ID
              );
              console.log(`Filtered ${originalCount} containers to ${response.value.length} matching containerTypeId`);
            }
          } catch (noFilterError) {
            attemptedMethods.push(`v1.0 without filter - FAILED: ${noFilterError instanceof Error ? noFilterError.message : String(noFilterError)}`);
            console.error("❌ All methods failed. Attempted methods:", attemptedMethods);
            throw noFilterError;
          }
        }
      }

      console.log("Container listing methods attempted:", attemptedMethods);
      return (response.value || []) as SPEContainer[];
      
    } catch (error) {
      console.error("Error listing SPE containers:", error);
      
      // Azure best practice: Enhanced error logging for better diagnostics
      if (error instanceof Error) {
        console.error("Container listing error details:", {
          message: error.message,
          containerTypeId: SPE_CONTAINER_TYPE_ID,
          stack: error.stack
        });
        
        if (error.message.includes('400')) {
          console.error("❌ Bad Request Error: This might indicate an issue with the filter syntax or endpoint");
          console.info("💡 Suggestion: Check if the containerTypeId format is correct or if the API endpoint has changed");
        }
        
        if (error.message.includes('Forbidden') || error.message.includes('admin operations')) {
          console.error("❌ Permission Error: User lacks permissions to list containers");
          console.info("💡 Suggestion: User needs proper SharePoint Embedded permissions or container type access");
        }
        
        if ('code' in error) {
          console.error("Error code:", (error as any).code);
        }
      }
      
      throw new Error(`Failed to list containers: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Finds existing container for a specific matter ID
   * Prevents duplicate container creation for the same matter
   * @param matterId Matter ID to search for
   * @returns Existing container or null if not found
   */
  public async findMatterContainer(matterId: string): Promise<SPEContainer | null> {
    try {
      console.info("Searching for existing container for matter:", matterId);
      
      // Get all matter containers
      const containers = await this.getMatterContainers();
      
      // Find containers that match the matter ID pattern
      const matterContainers = containers.filter(container => {
        // Check if container name starts with the matter ID pattern
        const matterPattern = `Matter-${matterId}-`;
        return container.displayName.startsWith(matterPattern);
      });
      
      if (matterContainers.length > 0) {
        if (matterContainers.length > 1) {
          console.warn(`Found ${matterContainers.length} containers for matter ${matterId}. Using the first one.`, 
            matterContainers.map(c => ({ id: c.id, name: c.displayName })));
        }
        
        console.info(`Found existing container for matter ${matterId}:`, matterContainers[0].id);
        return matterContainers[0];
      }
      
      console.info(`No existing container found for matter ${matterId}`);
      return null;
    } catch (error) {
      console.error("Error searching for matter container:", error);
      throw new Error(`Failed to search for matter container: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Uploads a file to a matter's container
   * @param file File to upload
   * @param containerId Container ID (required)
   * @param folder Optional folder path within container
   */
  public async uploadFile(file: File, containerId: string, folder: string = ""): Promise<SPEDocument> {
    if (!containerId) {
      throw new Error("Container ID is required");
    }

    try {
      console.log(`Uploading ${file.name} to container ${containerId}`);
      
      // Azure best practice: Use auth provider that's stored as a class property
      const token = await this.authProvider.getAccessToken();
      
      const fileArrayBuffer = await file.arrayBuffer();
      
      // Build the file path with optional folder
      const filePath = folder ? `${folder}/${encodeURIComponent(file.name)}` : encodeURIComponent(file.name);
      
      // Azure best practice: Use fetch API with proper headers for SharePoint Embedded
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/drives/${containerId}/root:/${filePath}:/content`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': file.type || 'application/octet-stream'
          },
          body: new Blob([fileArrayBuffer], { type: file.type || 'application/octet-stream' })
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log("File upload successful:", result);
      return result as SPEDocument;
    } catch (error) {
      console.error("Error uploading file:", error);
      
      // Azure best practice: Enhanced error reporting for API errors
      if (error instanceof Error) {
        console.error("Error details:", error.message);
      }
      
      throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Lists files in a matter's container
   * @param containerId Container ID
   * @param path Path within the container (optional)
   */
  public async listFiles(containerId: string, path: string = "/"): Promise<SPEDocument[]> {
    if (!containerId) {
      throw new Error("Container ID is required");
    }

    try {
      // Azure best practice: Use the stable v1.0 API endpoint when available
      let apiPath = `/drives/${containerId}/root/children`;
      if (path !== "/") {
        apiPath = `/drives/${containerId}/root:${path}:/children`;
      }
      
      // Azure best practice: Add proper logging for API calls
      console.info(`Fetching files from container ${containerId} at path ${path}`);
      
      const response = await this.client
        .api(apiPath)
        .version('v1.0')  // Use v1.0 endpoint instead of beta
        .expand('listitem($expand=fields)')  // Include expanded metadata fields
        .get();
      
      // Filter out folders - only return actual files
      const files = (response.value || []).filter((item: any) => item.file && !item.folder);
      
      // Azure best practice: Add debug logging to help with troubleshooting
      console.log(`Retrieved ${files.length} files from container (filtered out folders)`);
      
      return files as SPEDocument[];
    } catch (error) {
      console.error("Error listing files:", error);
      
      // Azure best practice: Enhanced error logging for better diagnostics
      if (error instanceof Error) {
        console.error("Error details:", {
          containerId,
          path,
          message: error.message,
          stack: error.stack
        });
        
        if (error.message.includes('Forbidden') || error.message.includes('access denied')) {
          console.error("❌ Permission Error: User lacks permissions to list files in this container");
          console.info("💡 Check if user is in the correct Microsoft 365 Group with write permissions");
          console.info("💡 Container ID:", containerId);
        }
        
        if ('code' in error) {
          console.error("Error code:", (error as any).code);
        }
      }
      
      throw new Error(`Failed to list files: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Downloads a file from a container
   * @param containerId Container ID
   * @param fileId File ID to download
   */
  public async downloadFile(containerId: string, fileId: string): Promise<{ blob: Blob; filename: string }> {
    try {
      // Get file metadata first to get the filename
      const fileInfo = await this.client
        .api(`/drives/${containerId}/items/${fileId}`)
        .version('v1.0')
        .get();

      // Get download URL
      const downloadResponse = await this.client
        .api(`/drives/${containerId}/items/${fileId}/content`)
        .version('v1.0')
        .getStream();

      // Convert stream to blob
      const chunks: Uint8Array[] = [];
      const reader = downloadResponse.getReader();
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      
      const blob = new Blob(chunks);
      
      return {
        blob,
        filename: fileInfo.name
      };
    } catch (error) {
      console.error("Error downloading file:", error);
      throw new Error(`Failed to download file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Deletes a file from a container
   * @param containerId Container ID
   * @param fileId File ID to delete
   */
  public async deleteFile(containerId: string, fileId: string): Promise<void> {
    try {
      await this.client
        .api(`/drives/${containerId}/items/${fileId}`)
        .version('v1.0')
        .delete();
        
      console.log("File deleted successfully:", fileId);
    } catch (error) {
      console.error("Error deleting file:", error);
      throw new Error(`Failed to delete file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Gets preview URL for a document with file-type specific handling and caching
   * @param containerId Container ID (driveId - starts with "b!")
   * @param fileId File ID (itemId)
   */
  public async getDocumentPreviewUrl(containerId: string, fileId: string): Promise<string> {
    // Check cache first
    const cacheKey = `${containerId}-${fileId}`;
    const cachedData = previewUrlCache.get(cacheKey);
    const now = Date.now();
    
    if (cachedData && (now - cachedData.timestamp < PREVIEW_URL_CACHE_DURATION)) {
      console.log(`🔗 Using cached preview URL for document ${fileId}`);
      return cachedData.url;
    }
    
    // Helper function to cache and return URL
    const cacheAndReturn = (url: string): string => {
      previewUrlCache.set(cacheKey, {
        url,
        timestamp: now
      });
      console.log(`💾 Cached preview URL for document ${fileId}`);
      return url;
    };
    
    // Try server-side endpoint first
    try {
      // Use server-side endpoint to avoid CORS issues with Graph API
      // Follow the same pattern as apiClient for URL construction
      let baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';
      
      if (typeof window !== 'undefined') {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          // Running locally - use environment variable or default
          baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';
        } else {
          // Running in production (Azure Static Web Apps or custom domain) - use relative /api path
          baseUrl = '/api';
        }
      }
      
      const url = `${baseUrl}/documents/preview?containerId=${encodeURIComponent(containerId)}&fileId=${encodeURIComponent(fileId)}`;
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/plain'
        }
      });

      if (response.ok) {
        const previewUrl = await response.text();
        
        // Apply SharePoint Embedded specific URL parameters to remove OneDrive branding
        const urlParams = new URLSearchParams();
        urlParams.set('nb', 'true');           // No banner
        urlParams.set('navpane', '0');         // Hide navigation pane
        urlParams.set('action', 'view');       // View mode only
        urlParams.set('wdAccPdf', '0');        // Disable PDF accessibility features that might show OneDrive branding
        urlParams.set('wdEmbedFS', '1');       // Enable full-screen embed mode
        
        // Add parameters to the preview URL
        const separator = previewUrl.includes('?') ? '&' : '?';
        const enhancedUrl = `${previewUrl}${separator}${urlParams.toString()}`;
        
        console.log("Enhanced preview URL with SharePoint Embedded parameters:", enhancedUrl);
        return cacheAndReturn(enhancedUrl);
      }
      
      console.warn(`Server preview API returned ${response.status}, falling back to client-side approach`);
    } catch (error) {
      console.warn("Server preview API failed, falling back to client-side approach:", error);
    }

    // Fallback to client-side approach with file-type specific handling
    try {
      console.log("Using client-side fallback for document preview");
      
      // Get file metadata which includes webUrl, downloadUrl, and file type info
      const fileResponse = await this.client
        .api(`/drives/${containerId}/items/${fileId}`)
        .version("v1.0")
        .get();

      console.log("File metadata retrieved:", {
        hasWebUrl: !!fileResponse.webUrl,
        hasDownloadUrl: !!fileResponse["@microsoft.graph.downloadUrl"],
        fileName: fileResponse.name,
        mimeType: fileResponse.file?.mimeType
      });

      // Determine file type for different handling approaches
      const fileName = fileResponse.name?.toLowerCase() || '';
      const mimeType = fileResponse.file?.mimeType || '';
      const isPdf = fileName.endsWith('.pdf') || mimeType === 'application/pdf';
      const isImage = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.svg', '.webp'].some(ext => fileName.endsWith(ext)) ||
                     mimeType.startsWith('image/');
      const isOfficeDoc = ['.docx', '.xlsx', '.pptx', '.doc', '.xls', '.ppt'].some(ext => fileName.endsWith(ext)) ||
                         mimeType.includes('officedocument') || 
                         mimeType.includes('ms-excel') || 
                         mimeType.includes('ms-powerpoint') || 
                         mimeType.includes('msword');

      // Helper function to add SharePoint Embedded parameters to remove OneDrive branding
      const addSPEParameters = (url: string): string => {
        const urlParams = new URLSearchParams();
        urlParams.set('nb', 'true');           // No banner
        urlParams.set('navpane', '0');         // Hide navigation pane
        urlParams.set('action', 'view');       // View mode only
        urlParams.set('wdAccPdf', '0');        // Disable PDF accessibility features
        urlParams.set('wdEmbedFS', '1');       // Enable full-screen embed mode
        
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${urlParams.toString()}`;
      };

      // Handle PDFs differently - they need special PDF viewer handling
      if (isPdf) {
        console.log("Handling PDF file with PDF-specific logic");
        
        // For PDFs, try preview API first to get Office Online PDF viewer URL
        try {
          const previewResponse = await this.client
            .api(`/drives/${containerId}/items/${fileId}/preview`)
            .version("v1.0")
            .post({});
          
          if (previewResponse.getUrl) {
            console.log("Using PDF preview URL from Graph API (Office Online PDF viewer)");
            return cacheAndReturn(addSPEParameters(previewResponse.getUrl));
          }
        } catch (previewError) {
          console.log("PDF preview API failed, trying download URL:", previewError);
        }
        
        // Fallback: Download URL for direct PDF viewing (will trigger download but works)
        if (fileResponse["@microsoft.graph.downloadUrl"]) {
          console.log("Using PDF download URL (will trigger download but accessible)");
          return fileResponse["@microsoft.graph.downloadUrl"];
        }
        
        // Last resort: webUrl (but this often causes access denied for PDFs)
        if (fileResponse.webUrl) {
          console.log("Using PDF webUrl as last resort (may cause access denied)");
          return addSPEParameters(fileResponse.webUrl);
        }
      }

      // Handle images - use download URL directly for display
      if (isImage) {
        console.log("Handling image file - using direct download URL");
        
        if (fileResponse["@microsoft.graph.downloadUrl"]) {
          console.log("Using image download URL for direct display");
          return cacheAndReturn(fileResponse["@microsoft.graph.downloadUrl"]);
        }
        
        // Fallback to webUrl if download URL not available
        if (fileResponse.webUrl) {
          console.log("Using image webUrl as fallback");
          return fileResponse.webUrl;
        }
      }

      // Handle Office documents - they work well with Office Online
      if (isOfficeDoc) {
        console.log("Handling Office document with Office Online integration");
        
        // For Office docs, webUrl works best (Office Online viewer)
        if (fileResponse.webUrl) {
          console.log("Using Office Online webUrl");
          return addSPEParameters(fileResponse.webUrl);
        }
        
        // Fallback to download URL for Office documents
        if (fileResponse["@microsoft.graph.downloadUrl"]) {
          console.log("Using Office document download URL fallback");
          return fileResponse["@microsoft.graph.downloadUrl"];
        }
      }

      // Default handling for other file types
      console.log("Using default file handling for other file types");
      
      // Try webUrl first, then download URL
      if (fileResponse.webUrl) {
        console.log("Using default webUrl");
        return addSPEParameters(fileResponse.webUrl);
      }
      
      if (fileResponse["@microsoft.graph.downloadUrl"]) {
        console.log("Using default download URL");
        return fileResponse["@microsoft.graph.downloadUrl"];
      }
      
      throw new Error("No preview URL available from file metadata");
    } catch (fallbackError) {
      console.error("Client-side fallback failed:", fallbackError);
      throw new Error("Unable to get preview URL for document");
    }
  }

  /**
   * Gets embed URL for Office documents using server-side endpoint
   * @param containerId Container ID (driveId - starts with "b!")
   * @param fileId File ID (itemId)
   */
  public async getDocumentEmbedUrl(containerId: string, fileId: string): Promise<string> {
    try {
      // Use the same preview endpoint since it provides embeddable URLs
      return await this.getDocumentPreviewUrl(containerId, fileId);
    } catch (error) {
      console.error("Error getting embed URL:", error);
      throw error;
    }
  }

  /**
   * Gets download URL for a document
   * @param containerId Container ID (driveId - starts with "b!")
   * @param fileId File ID (itemId)
   */
  public async getDocumentDownloadUrl(containerId: string, fileId: string): Promise<string> {
    try {
      // Get file metadata which includes the download URL
      const fileMetadata = await this.client
        .api(`/drives/${containerId}/items/${fileId}`)
        .version('v1.0')
        .get();

      if (fileMetadata['@microsoft.graph.downloadUrl']) {
        return fileMetadata['@microsoft.graph.downloadUrl'];
      }

      // If no download URL in metadata, use webUrl as fallback
      if (fileMetadata.webUrl) {
        return fileMetadata.webUrl;
      }

      throw new Error('No download URL available for this document');
    } catch (error) {
      console.error("Error getting download URL:", error);
      throw error;
    }
  }

  /**
   * Deletes a SharePoint Embedded container (use with caution)
   * @param containerId The ID of the container to delete
   * @returns Promise that resolves when the container has been deleted
   */
  public async deleteContainer(containerId: string): Promise<void> {
    if (!containerId) {
      throw new Error("Container ID is required for deletion");
    }

    try {
      console.warn("Deleting SharePoint Embedded container:", containerId);
      
      await this.client
        .api(`/storage/fileStorage/containers/${containerId}`)
        .version('v1.0')
        .delete();
        
      console.info("Container deleted successfully:", containerId);
      console.warn("⚠️ Container moved to recycle bin - still counts against trial quota limit");
      console.info("💡 To free up quota: Delete permanently from SharePoint Admin Center > Container Management");
    } catch (error) {
      console.error("Error deleting SPE container:", error);
      throw new Error(`Failed to delete container: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Gets the current container count and quota information
   * Helps identify quota issues with trial container types
   */
  public async getContainerQuotaInfo(): Promise<{
    currentCount: number;
    limit: number;
    availableSlots: number;
    isNearLimit: boolean;
    containerTypeId: string;
  }> {
    try {
      const containers = await this.getMatterContainers();
      const currentCount = containers.length;
      const trialLimit = 5; // Trial container type limit
      
      return {
        currentCount,
        limit: trialLimit,
        availableSlots: Math.max(0, trialLimit - currentCount),
        isNearLimit: currentCount >= trialLimit - 1,
        containerTypeId: SPE_CONTAINER_TYPE_ID
      };
    } catch (error) {
      console.error("Error getting container quota info:", error);
      throw new Error(`Failed to get container quota info: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Assign group permissions to a SharePoint Embedded container
   * Uses the configured group from settings
   */
  public async assignGroupPermissionToContainer(containerId: string): Promise<void> {
    try {
      const settings = settingsService.getSharePointEmbeddedSettings();
      const groupId = settings.defaultGroupId;

      if (!groupId) {
        throw new Error('No default group configured for container permissions. Please configure in settings.');
      }

      console.log(`Assigning group ${settings.defaultGroupEmail} (${settings.defaultGroupAlias}) write permissions to container ${containerId}`);

      // Use the exact format that works in your Postman example
      // Reference: Your working Postman example
      const permissionRequest = {
        roles: ['writer'],
        grantedToV2: {
          user: {
            userPrincipalName: settings.defaultGroupEmail  // Use the group's User Principal Name
          }
        }
      };

      await this.client
        .api(`/storage/fileStorage/containers/${containerId}/permissions`)
        .version('beta')  // Use beta endpoint as shown in Microsoft documentation
        .post(permissionRequest);

      console.log(`Successfully assigned group permissions to container ${containerId}`);
    } catch (error) {
      console.error('Error assigning group permission to container:', error);
      throw new Error(`Failed to assign group permission: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * List all permissions for a SharePoint Embedded container
   */
  public async listContainerPermissions(containerId: string): Promise<SPEContainerPermission[]> {
    try {
      const response = await this.client
        .api(`/storage/fileStorage/containers/${containerId}/permissions`)
        .version('beta')  // Use beta endpoint for container permissions
        .get();

      return response.value || [];
    } catch (error) {
      console.error('Error listing container permissions:', error);
      throw new Error(`Failed to list container permissions: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Check if the configured group has permissions on a container
   */
  public async hasGroupPermission(containerId: string): Promise<boolean> {
    try {
      const settings = settingsService.getSharePointEmbeddedSettings();
      const groupId = settings.defaultGroupId;

      if (!groupId) {
        return false;
      }

      const permissions = await this.listContainerPermissions(containerId);
      return permissions.some(permission => 
        permission.grantedToV2.group?.id === groupId &&
        permission.roles.includes('write')
      );
    } catch (error) {
      console.error('Error checking group permission:', error);
      return false;
    }
  }

  /**
   * Check if the current user is a member of the configured Microsoft 365 Group
   */
  public async isUserInConfiguredGroup(): Promise<{ isMember: boolean; groupId: string | null; userInfo: any; error?: string }> {
    try {
      const settings = settingsService.getSharePointEmbeddedSettings();
      const groupId = settings.defaultGroupId;

      if (!groupId) {
        return {
          isMember: false,
          groupId: null,
          userInfo: null,
          error: 'No group configured in settings'
        };
      }

      // Get current user info
      const token = await this.authProvider.getAccessToken();
      const userResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!userResponse.ok) {
        throw new Error(`Failed to get user info: ${userResponse.status}`);
      }

      const userInfo = await userResponse.json();

      // Check if user is a member of the group
      const membershipResponse = await fetch(`https://graph.microsoft.com/v1.0/groups/${groupId}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!membershipResponse.ok) {
        throw new Error(`Failed to check group membership: ${membershipResponse.status}`);
      }

      const membershipData = await membershipResponse.json();
      const isMember = membershipData.value.some((member: any) => member.id === userInfo.id);

      console.log(`User ${userInfo.displayName} (${userInfo.userPrincipalName}) membership in group ${groupId}:`, isMember);

      return {
        isMember,
        groupId,
        userInfo,
      };
    } catch (error) {
      console.error('Error checking user group membership:', error);
      return {
        isMember: false,
        groupId: null,
        userInfo: null,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  // =====================================
  // FOLDER OPERATIONS - Phase 3 Enhancement
  // =====================================

  /**
   * Creates a new folder in a SharePoint Embedded container
   * Supports hybrid navigation with configurable conflict resolution
   * 
   * @param containerId Container ID where to create the folder
   * @param folderName Name of the folder to create
   * @param parentPath Optional parent folder path (default: root)
   * @param config Optional configuration for folder creation
   */
  public async createFolder(
    containerId: string,
    folderName: string,
    parentPath: string = '',
    config: FolderOperationConfig = { conflictBehavior: 'rename', validateDepth: true, maxDepth: 3 }
  ): Promise<SPEFolder> {
    try {
      if (!containerId || !folderName) {
        throw new Error("Container ID and folder name are required");
      }

      // Validate folder depth if enabled
      if (config.validateDepth && config.maxDepth) {
        const depth = this.calculateFolderDepth(parentPath);
        if (depth >= config.maxDepth) {
          throw new Error(`Maximum folder depth of ${config.maxDepth} levels exceeded. Current depth: ${depth + 1}`);
        }
      }

      // Sanitize folder name
      const sanitizedFolderName = this.sanitizeFolderName(folderName);
      
      // Build API endpoint based on parent path
      // Following Microsoft Graph API documentation: POST /drives/{drive-id}/root/children
      let apiPath: string;
      if (!parentPath || parentPath === '' || parentPath === '/') {
        // Create in root folder - matches Microsoft's example: /me/drive/root/children
        apiPath = `/drives/${containerId}/root/children`;
      } else {
        // For nested folders, use path-based approach: /drives/{drive-id}/root:{path}:/children
        const cleanPath = parentPath.startsWith('/') ? parentPath : `/${parentPath}`;
        apiPath = `/drives/${containerId}/root:${cleanPath}:/children`;
      }

      // Prepare request payload
      const requestPayload = {
        name: sanitizedFolderName,
        folder: {},
        "@microsoft.graph.conflictBehavior": config.conflictBehavior
      };

      console.info(`Creating folder "${sanitizedFolderName}" in container ${containerId} at path: ${parentPath || 'root'}`);
      console.debug("Folder creation request:", { apiPath, payload: requestPayload });

      // Make API call to create folder
      const response = await this.client
        .api(apiPath)
        .version('v1.0')
        .post(requestPayload);

      console.info(`Folder created successfully: ${response.id}`);

      // Build folder path for hybrid navigation
      const folderPath = this.buildFolderPath(parentPath, sanitizedFolderName);

      // Transform response to SPEFolder interface
      const folder: SPEFolder = {
        id: response.id,
        name: response.name,
        path: folderPath,
        parentPath: parentPath || '',
        createdDateTime: response.createdDateTime,
        lastModifiedDateTime: response.lastModifiedDateTime,
        webUrl: response.webUrl,
        createdBy: response.createdBy,
        lastModifiedBy: response.lastModifiedBy
      };

      return folder;
    } catch (error) {
      console.error("Error creating folder:", error);
      
      // Enhanced error handling for folder creation
      if (error instanceof Error) {
        if (error.message.includes('nameAlreadyExists') || error.message.includes('conflict')) {
          throw new Error(`A folder named "${folderName}" already exists in this location. Try a different name or use "replace" conflict behavior.`);
        }
        if (error.message.includes('invalidFolderName')) {
          throw new Error(`Invalid folder name "${folderName}". Folder names cannot contain: \\ / : * ? " < > |`);
        }
        if (error.message.includes('Forbidden') || error.message.includes('access denied')) {
          throw new Error(`Access denied creating folder. Check user permissions for container: ${containerId}`);
        }
      }

      throw new Error(`Failed to create folder: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Lists all folders in a container or specific path
   * Optimized for performance with selective loading
   * 
   * @param containerId Container ID to list folders from
   * @param path Optional path to list folders from (default: root)
   */
  public async listFolders(containerId: string, path: string = ''): Promise<SPEFolder[]> {
    try {
      if (!containerId) {
        throw new Error("Container ID is required");
      }

      // Build API endpoint
      let apiPath: string;
      if (!path || path === '' || path === '/') {
        apiPath = `/drives/${containerId}/root/children`;
      } else {
        // For SharePoint Embedded, try the folder ID approach first
        const cleanPath = path.startsWith('/') ? path.substring(1) : path;
        
        try {
          const folderResponse = await this.client
            .api(`/drives/${containerId}/root:/${cleanPath}`)
            .version('v1.0')
            .select('id,name')
            .get();
          
          // Now use the folder ID to list its children folders
          apiPath = `/drives/${containerId}/items/${folderResponse.id}/children`;
          console.info(`Using folder ID ${folderResponse.id} to list subfolders of "${cleanPath}"`);
        } catch (folderError) {
          console.warn(`Failed to get folder ID for "${cleanPath}", falling back to path-based approach:`, folderError);
          // Fallback to direct path approach
          const cleanPathWithSlash = path.startsWith('/') ? path : `/${path}`;
          apiPath = `/drives/${containerId}/root:${cleanPathWithSlash}:/children`;
        }
      }

      console.info(`Listing folders in container ${containerId} at path: ${path || 'root'}`);

      // Fetch items - try simplified approach first for folder ID method
      let response;
      if (apiPath.includes('/items/')) {
        // For folder ID approach, use simpler query without filters
        response = await this.client
          .api(apiPath)
          .version('v1.0')
          .select('id,name,createdDateTime,lastModifiedDateTime,webUrl,createdBy,lastModifiedBy,folder')
          .get();
        
        // Filter for folders only in client code since server-side filter might not work
        response.value = (response.value || []).filter((item: any) => item.folder);
      } else {
        // For path-based approach, use original query with filters
        response = await this.client
          .api(apiPath)
          .version('v1.0')
          .filter('folder ne null')  // Only get folders
          .select('id,name,createdDateTime,lastModifiedDateTime,webUrl,createdBy,lastModifiedBy,folder')
          .get();
      }

      const folders: SPEFolder[] = (response.value || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        path: this.buildFolderPath(path, item.name),
        parentPath: path || '',
        createdDateTime: item.createdDateTime,
        lastModifiedDateTime: item.lastModifiedDateTime,
        webUrl: item.webUrl,
        itemCount: item.folder?.childCount,
        createdBy: item.createdBy,
        lastModifiedBy: item.lastModifiedBy
      }));

      console.info(`Found ${folders.length} folders in path: ${path || 'root'}`);
      return folders;
    } catch (error) {
      console.error("Error listing folders:", error);
      throw new Error(`Failed to list folders: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Deletes a folder from a container
   * 
   * @param containerId Container ID containing the folder
   * @param folderId Folder ID to delete
   */
  public async deleteFolder(containerId: string, folderId: string): Promise<void> {
    try {
      if (!containerId || !folderId) {
        throw new Error("Container ID and folder ID are required");
      }

      console.info(`Deleting folder ${folderId} from container ${containerId}`);

      await this.client
        .api(`/drives/${containerId}/items/${folderId}`)
        .version('v1.0')
        .delete();

      console.info(`Folder deleted successfully: ${folderId}`);
    } catch (error) {
      console.error("Error deleting folder:", error);
      
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('not found')) {
          throw new Error(`Folder not found. It may have already been deleted.`);
        }
        if (error.message.includes('Forbidden') || error.message.includes('access denied')) {
          throw new Error(`Access denied deleting folder. Check user permissions.`);
        }
        if (error.message.includes('folderNotEmpty')) {
          throw new Error(`Folder cannot be deleted because it contains files or subfolders. Delete contents first.`);
        }
      }

      throw new Error(`Failed to delete folder: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Moves a document to a different folder
   * Supports both path-based and ID-based targeting
   * 
   * @param containerId Container ID containing the document
   * @param documentId Document ID to move
   * @param targetPath Target folder path or folder ID
   * @param useIdBasedTarget Whether targetPath is a folder ID (true) or path (false)
   */
  public async moveDocument(
    containerId: string, 
    documentId: string, 
    targetPath: string,
    useIdBasedTarget: boolean = false
  ): Promise<SPEDocument> {
    try {
      if (!containerId || !documentId || !targetPath) {
        throw new Error("Container ID, document ID, and target path are required");
      }

      console.info(`Moving document ${documentId} to ${targetPath} in container ${containerId}`);

      // Build parent reference based on target type
      let parentReference: any;
      if (useIdBasedTarget) {
        // ID-based targeting for performance
        parentReference = {
          id: targetPath
        };
      } else {
        // Path-based targeting for flexibility
        const cleanPath = targetPath === '/' ? '' : (targetPath.startsWith('/') ? targetPath : `/${targetPath}`);
        if (cleanPath === '') {
          parentReference = {
            path: `/drives/${containerId}/root`
          };
        } else {
          parentReference = {
            path: `/drives/${containerId}/root:${cleanPath}`
          };
        }
      }

      // Move the document
      const response = await this.client
        .api(`/drives/${containerId}/items/${documentId}`)
        .version('v1.0')
        .patch({
          parentReference: parentReference
        });

      console.info(`Document moved successfully: ${response.id}`);
      return response as SPEDocument;
    } catch (error) {
      console.error("Error moving document:", error);
      
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('not found')) {
          throw new Error(`Document or target folder not found.`);
        }
        if (error.message.includes('Forbidden') || error.message.includes('access denied')) {
          throw new Error(`Access denied moving document. Check user permissions.`);
        }
      }

      throw new Error(`Failed to move document: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Lists documents in a specific folder
   * Optimized for folder-based document management
   * 
   * @param containerId Container ID
   * @param folderPath Folder path to list documents from
   */
  public async listDocumentsInFolder(containerId: string, folderPath: string = ''): Promise<SPEDocument[]> {
    try {
      if (!containerId) {
        throw new Error("Container ID is required");
      }

      // Build API endpoint to match the working folder creation pattern
      let apiPath: string;
      if (!folderPath || folderPath === '' || folderPath === '/') {
        apiPath = `/drives/${containerId}/root/children`;
      } else {
        // For SharePoint Embedded, try a simpler approach first
        // Remove leading slash and use the items approach
        const cleanPath = folderPath.startsWith('/') ? folderPath.substring(1) : folderPath;
        
        // Try to get the folder first to get its ID, then list its children
        try {
          const folderResponse = await this.client
            .api(`/drives/${containerId}/root:/${cleanPath}`)
            .version('v1.0')
            .select('id,name')
            .get();
          
          // Now use the folder ID to list its children
          apiPath = `/drives/${containerId}/items/${folderResponse.id}/children`;
          console.info(`Using folder ID ${folderResponse.id} to list contents of "${cleanPath}"`);
        } catch (folderError) {
          console.warn(`Failed to get folder ID for "${cleanPath}", falling back to path-based approach:`, folderError);
          // Fallback to direct path approach
          const cleanPathWithSlash = folderPath.startsWith('/') ? folderPath : `/${folderPath}`;
          apiPath = `/drives/${containerId}/root:${cleanPathWithSlash}:/children`;
        }
      }

      console.info(`Listing documents in folder "${folderPath || 'root'}" for container ${containerId}`);

      // Fetch items - simplified approach without problematic filters
      let response;
      if (apiPath.includes('/items/')) {
        // For folder ID approach, use simpler query without filters
        response = await this.client
          .api(apiPath)
          .version('v1.0')
          .select('id,name,size,createdDateTime,lastModifiedDateTime,file,folder,webUrl,createdBy,lastModifiedBy')
          .get();
      } else {
        // For path-based approach, also use simpler query without problematic filters
        response = await this.client
          .api(apiPath)
          .version('v1.0')
          .select('id,name,size,createdDateTime,lastModifiedDateTime,file,folder,webUrl,createdBy,lastModifiedBy')
          .get();
      }

      // Filter for files only in client code to avoid Graph API limitations
      const documents: SPEDocument[] = (response.value || []).filter((item: any) => item.file && !item.folder);
      
      console.info(`Found ${documents.length} documents in folder: ${folderPath || 'root'}`);
      return documents;
    } catch (error) {
      console.error("Error listing documents in folder:", error);
      throw new Error(`Failed to list documents in folder: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Gets folder information by path or ID
   * Supports hybrid navigation approach
   * 
   * @param containerId Container ID
   * @param folderIdentifier Folder path or ID
   * @param useIdBased Whether to use ID-based lookup (true) or path-based (false)
   */
  public async getFolderInfo(
    containerId: string, 
    folderIdentifier: string,
    useIdBased: boolean = false
  ): Promise<SPEFolder | null> {
    try {
      if (!containerId || !folderIdentifier) {
        throw new Error("Container ID and folder identifier are required");
      }

      let apiPath: string;
      if (useIdBased) {
        apiPath = `/drives/${containerId}/items/${folderIdentifier}`;
      } else {
        const cleanPath = folderIdentifier.startsWith('/') ? folderIdentifier : `/${folderIdentifier}`;
        apiPath = `/drives/${containerId}/root:${cleanPath}`;
      }

      const response = await this.client
        .api(apiPath)
        .version('v1.0')
        .select('id,name,createdDateTime,lastModifiedDateTime,webUrl,createdBy,lastModifiedBy,folder')
        .get();

      if (!response.folder) {
        throw new Error("Specified item is not a folder");
      }

      // Calculate parent path for hybrid navigation
      const pathParts = folderIdentifier.split('/').filter(part => part !== '');
      const parentPath = pathParts.length > 1 ? `/${pathParts.slice(0, -1).join('/')}` : '';

      const folder: SPEFolder = {
        id: response.id,
        name: response.name,
        path: useIdBased ? folderIdentifier : folderIdentifier,  // Keep original identifier as path
        parentPath: parentPath,
        createdDateTime: response.createdDateTime,
        lastModifiedDateTime: response.lastModifiedDateTime,
        webUrl: response.webUrl,
        itemCount: response.folder?.childCount,
        createdBy: response.createdBy,
        lastModifiedBy: response.lastModifiedBy
      };

      return folder;
    } catch (error) {
      console.error("Error getting folder info:", error);
      
      if (error instanceof Error && (error.message.includes('404') || error.message.includes('not found'))) {
        return null;
      }
      
      throw new Error(`Failed to get folder info: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // =====================================
  // FOLDER UTILITY METHODS
  // =====================================

  /**
   * Calculates the depth of a folder path
   * Used for depth validation (max 3 levels)
   */
  private calculateFolderDepth(path: string): number {
    if (!path || path === '' || path === '/') {
      return 0;
    }
    
    const cleanPath = path.replace(/^\/+|\/+$/g, ''); // Remove leading/trailing slashes
    return cleanPath === '' ? 0 : cleanPath.split('/').length;
  }

  /**
   * Sanitizes folder name to remove invalid characters
   * SharePoint/OneDrive doesn't allow: \ / : * ? " < > |
   */
  private sanitizeFolderName(name: string): string {
    return name
      .replace(/[\\/:*?"<>|]/g, '_')  // Replace invalid chars with underscore
      .replace(/\s+/g, ' ')          // Normalize whitespace
      .trim()                        // Remove leading/trailing whitespace
      .substring(0, 255);            // Limit length to 255 characters
  }

  /**
   * Builds a folder path for hybrid navigation
   * Combines parent path with folder name
   */
  private buildFolderPath(parentPath: string, folderName: string): string {
    if (!parentPath || parentPath === '' || parentPath === '/') {
      return `/${folderName}`;
    }
    
    const cleanParentPath = parentPath.startsWith('/') ? parentPath : `/${parentPath}`;
    return `${cleanParentPath}/${folderName}`;
  }
}

export default SharePointEmbeddedService;
